import {
  Action,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import SearchService from '@/services/SearchService';
import SearchRequest from '@/requests/SearchRequest';
import SearchResponse from '@/requests/SearchResponse';
import SupportedLocale from '@/types/SupportedLocale';
import SupportedContext from '@/types/SupportedContext';
import SearchContextData from '@/models/SearchContextData';
import SearchResultElement from '@/models/SearchResultElement';
import SearchDefaults from '@/types/SearchDefaults';
import RootState from '@/store/states/Root';
import SearchState from '@/store/states/Search';
import { ActionContext } from 'vuex';
import SupportedCountry from '@/types/SupportedCountry';
import Vue from 'vue';
import SearchSuggest from '@/models/SearchSuggest';
import RouteData from '@/models/RouteData';
import { BaseGetter, FilterGetter, SearchGetter } from '@/store/getters';
import { GeneralAction, SearchAction } from '@/store/actions';
import FilterOption from '@/models/FilterOption';
import InitDataExtended from '@/models/InitDataExtended';
import OriginalSearch from '@/models/OriginalSearch';

enum Mutators {
  HandleUpdateSearchResults = 'search.handleUpdateSearchResults',
  HandleCategoriesSearchResponse = 'search.handleCategoriesSearchResults',
  HandleExternalSearchResponse = 'search.handleExternalSearchResults',
  HandleProductSearchResponse = 'search.handleProductSearchResults',
  HandleIncrementOffset = 'search.handleIncrementOffset',
  HandleUpdateSearchContext = 'search.handleUpdateSearchContext',
  HandleSetViewed = 'search.handleSetViewed',
  HandleUpdateSearchTerm = 'search.handleUpdateSearchTerm',
  HandleUpdateSearchIncrement = 'search.handleUpdateSearchIncrement',
  HandleUpdateSearchContextData = 'search.handleUpdateSearchContextData',
  HandleUpdateStrictSearch = 'search.handleUpdateStrictSearch',
  HandleCategoriesIncrementOffset = 'search.handleCategoryIncrementOffset',
  HandleExternalIncrementOffset = 'search.handleExternalIncrementOffset',
  HandleProductsIncrementOffset = 'search.handleProductsIncrementOffset',
  UpdatePrev = 'search.handleUpdatePrev',
}

@Module
export default class SearchModule extends VuexModule<SearchState> {
  private searchTerm = '';

  private strictSearch = 0;

  private searchIncrement: number;

  private categoriesIncrement: number;

  private productsIncrement: number;

  private externalIncrement: number;

  private activeSearchContext: SupportedContext;

  private activeSearchContextData: SearchContextData;

  private activeCategoriesSearchContextData: SearchContextData;

  private activeExternalSearchContextData: SearchContextData;

  private activeProductSearchContextData: SearchContextData;

  private prevSent = false;

  constructor(module: SearchModule) {
    super(module);

    this.searchIncrement = SearchDefaults.increment;
    this.categoriesIncrement = SearchDefaults.categoriesIncrement;
    this.productsIncrement = SearchDefaults.productsIncrement;
    this.externalIncrement = SearchDefaults.externalIncrement;
    this.activeSearchContext = SearchDefaults.searchContext;
    this.activeSearchContextData = SearchModule.buildEmptyContextData(
      this.activeSearchContext,
      this.searchIncrement,
      true,
    );
    this.activeCategoriesSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.CATEGORY,
      this.categoriesIncrement,
      true,
    );
    this.activeExternalSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.EXTERNAL,
      this.externalIncrement,
      true,
    );
    this.activeProductSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.PRODUCT,
      this.productsIncrement,
      true,
    );
  }

  get [SearchGetter.ActiveSearchContextData](): SearchContextData {
    return this.activeSearchContextData;
  }

  get [SearchGetter.SearchResults](): Array<SearchResultElement> {
    return this.activeSearchContextData.elements;
  }

  get [SearchGetter.CategoriesSearchResults](): Array<SearchResultElement> {
    return this.activeCategoriesSearchContextData.elements;
  }

  get [SearchGetter.CategoriesTotalNumberOfResults](): number {
    return this.activeCategoriesSearchContextData.numberOfResults || 0;
  }

  get [SearchGetter.ExternalSearchResults](): Array<SearchResultElement> {
    return this.activeExternalSearchContextData.elements;
  }

  get [SearchGetter.ProductSearchResults](): Array<SearchResultElement> {
    return this.activeProductSearchContextData.elements;
  }

  get [SearchGetter.ExternalTotalNumberOfResults](): number {
    return this.activeExternalSearchContextData.numberOfResults || 0;
  }

  get [SearchGetter.ProductTotalNumberOfResults](): number {
    return this.activeProductSearchContextData.numberOfResults || 0;
  }

  get [SearchGetter.SearchSuggestions](): SearchSuggest {
    return this.activeSearchContextData.searchSuggest;
  }

  get [SearchGetter.OriginalSearch](): OriginalSearch {
    return this.activeSearchContextData.originalSearch;
  }

  get [SearchGetter.TotalNumberOfResults](): number {
    return this.activeSearchContextData.numberOfResults || 0;
  }

  get [SearchGetter.TotalNumberOfSuggestedResults](): number {
    return this.activeSearchContextData.searchSuggest.numberOfResults;
  }

  get [SearchGetter.IsLoading](): boolean {
    return this.activeSearchContextData.loading;
  }

  get [SearchGetter.CategoriesIsLoading](): boolean {
    return this.activeCategoriesSearchContextData.loading;
  }

  get [SearchGetter.ProductsIsLoading](): boolean {
    return this.activeProductSearchContextData.loading;
  }

  get [SearchGetter.ExternalIsLoading](): boolean {
    return this.activeExternalSearchContextData.loading;
  }

  get [SearchGetter.SearchTerm](): string {
    return this.searchTerm;
  }

  get [SearchGetter.StrictSearch](): number {
    return this.strictSearch;
  }

  private static buildSearchRequest(
    searchContextData: SearchContextData,
    searchTerm: string,
    locale: SupportedLocale,
    country: SupportedCountry,
    filters: Map<string, FilterOption>,
    strictSearch: number,
  ): SearchRequest {
    return {
      searchTerm,
      offset: searchContextData.offset,
      limit: searchContextData.limit,
      locale,
      type: searchContextData.searchContext,
      country,
      filters,
      strictSearch,
      limitP: SearchDefaults.productsIncrement,
      limitE: SearchDefaults.externalIncrement,
      limitC: SearchDefaults.categoriesIncrement,
    };
  }

  private static buildEmptyContextData(
    searchContext: SupportedContext,
    increment: number,
    loading: boolean,
  ): SearchContextData {
    return {
      searchContext,
      loading,
      offset: 0,
      limit: increment,
      numberOfResults: 0,
      elements: [],
      searchSuggest: {
        term: '',
        elements: [],
        numberOfResults: 0,
      },
      originalSearch: {
        count: 0,
        switchedTerm: '',
        term: '',
      },
    };
  }

  private static async doListSearch(
    context: ActionContext<SearchState, RootState>,
    searchContextData: SearchContextData,
    searchTerm: string,
    locale: SupportedLocale,
    country: SupportedCountry,
    filters: Map<string, FilterOption>,
    strictSearch: number,
  ): Promise<SearchResponse> {
    return new Promise<SearchResponse>((resolve) => {
      SearchService.doSearch(SearchModule.buildSearchRequest(
        searchContextData,
        searchTerm,
        locale,
        country,
        filters,
        strictSearch,
      )).then(async (responseData: SearchResponse) => {
        await context.dispatch(GeneralAction.HandleSearchResponse, {
          responseData,
        });
        resolve();
      }).catch((error: Error) => {
        console.log(error);
        console.warn(`Search action encountered an error: ${error}`);
        resolve();
      });
    });
  }

  private static async doSearch(
    context: ActionContext<SearchState, RootState>,
    searchContextData: SearchContextData,
    searchTerm: string,
    locale: SupportedLocale,
    country: SupportedCountry,
    filters: Map<string, FilterOption>,
    strictSearch: number,
  ): Promise<SearchResponse> {
    return new Promise<SearchResponse>((resolve) => {
      const sReq = SearchModule.buildSearchRequest(
        searchContextData,
        searchTerm,
        locale,
        country,
        filters,
        strictSearch,
      );

      SearchService.doSearchGrouped(sReq).then(async (responseData: SearchResponse) => {
        await context.dispatch(GeneralAction.HandleCategoriesSearchResponse, {
          responseData,
        });
        await context.dispatch(GeneralAction.HandleProductSearchResponse, {
          responseData,
        });
        await context.dispatch(GeneralAction.HandleExternalSearchResponse, {
          responseData,
        });
        resolve();
      }).catch((error: Error) => {
        console.log(error);
        console.warn(`Search action encountered an error: ${error}`);
        resolve();
      });

      SearchService.doSearch(sReq).then(async (responseData: SearchResponse) => {
        await context.dispatch(GeneralAction.HandleSearchResponse, {
          responseData,
        });
        resolve();
      }).catch((error: Error) => {
        console.log(error);
        console.warn(`Search action encountered an error: ${error}`);
        resolve();
      });
    });
  }

  @Mutation
  private [Mutators.HandleUpdateSearchIncrement](payload: { searchIncrement: number }) {
    this.searchIncrement = payload.searchIncrement;
  }

  @Mutation
  private [Mutators.HandleUpdateSearchContext](payload: { searchContext: SupportedContext }) {
    this.activeSearchContext = payload.searchContext;
  }

  @Mutation
  private [Mutators.HandleUpdateSearchTerm](payload: { searchTerm: string }) {
    this.searchTerm = payload.searchTerm;
  }

  @Mutation
  private [Mutators.HandleUpdateSearchContextData]() {
    this.activeSearchContextData = SearchModule.buildEmptyContextData(
      this.activeSearchContext,
      this.searchIncrement,
      this.searchTerm.length > 0,
    );
    this.activeCategoriesSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.CATEGORY,
      SearchDefaults.categoriesIncrement,
      this.searchTerm.length > 0,
    );
    this.activeProductSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.PRODUCT,
      SearchDefaults.productsIncrement,
      this.searchTerm.length > 0,
    );
    this.activeExternalSearchContextData = SearchModule.buildEmptyContextData(
      SupportedContext.EXTERNAL,
      SearchDefaults.externalIncrement,
      this.searchTerm.length > 0,
    );
  }

  @Mutation
  private [Mutators.HandleUpdateSearchResults](
    payload: {
      responseData: SearchResponse;
    },
  ) {
    this.activeSearchContextData.elements.push(...payload.responseData.elements.map(
      (searchResult): SearchResultElement => ({ ...searchResult, viewed: false }),
    ));
    this.activeSearchContextData.numberOfResults = payload.responseData.numberOfResults;
    this.activeSearchContextData.loading = false;
    Vue.set(this.activeSearchContextData, 'searchSuggest', payload.responseData.suggestion || {
      term: '',
      elements: [],
      numberOfResults: 0,
    });

    if (payload.responseData.originalSearch?.switchedTerm !== '') {
      Vue.set(this, 'searchTerm', payload.responseData.originalSearch?.switchedTerm);
    }
    Vue.set(this.activeSearchContextData, 'originalSearch', payload.responseData.originalSearch || {
      count: 0,
      switchedTerm: '',
      term: '',
    });
  }

  @Mutation
  private [Mutators.HandleCategoriesSearchResponse](
    payload: SearchResponse,
  ) {
    this.activeCategoriesSearchContextData.elements
      .push(...payload.elements.map(
        (searchResult): SearchResultElement => ({ ...searchResult, viewed: false }),
      ));
    // eslint-disable-next-line max-len
    this.activeCategoriesSearchContextData.numberOfResults = payload.numberOfResults;

    this.activeCategoriesSearchContextData.loading = false;
  }

  @Mutation
  private [Mutators.HandleExternalSearchResponse](
    payload: SearchResponse,
  ) {
    this.activeExternalSearchContextData.elements.push(...payload.elements.map(
      (searchResult): SearchResultElement => ({ ...searchResult, viewed: false }),
    ));
    this.activeExternalSearchContextData.numberOfResults = payload.numberOfResults;
    this.activeExternalSearchContextData.loading = false;
  }

  @Mutation
  private [Mutators.HandleProductSearchResponse](
    payload: SearchResponse,
  ) {
    this.activeProductSearchContextData.elements.push(...payload.elements.map(
      (searchResult): SearchResultElement => ({ ...searchResult, viewed: false }),
    ));
    this.activeProductSearchContextData.numberOfResults = payload.numberOfResults;
    this.activeProductSearchContextData.loading = false;
  }

  @Mutation
  private [Mutators.HandleUpdateStrictSearch](strictSearch: number) {
    this.strictSearch = strictSearch;
  }

  @Mutation
  private [Mutators.UpdatePrev](payload: boolean) {
    this.prevSent = payload;
  }

  @Action
  private [GeneralAction.UpdateSearchResults](payload: {
    responseData: SearchResponse;
  }) {
    this.context.commit(Mutators.HandleUpdateSearchResults, payload);

    if (!this.prevSent) {
      if (window.dataLayer !== undefined) {
        this.context.commit(Mutators.UpdatePrev, true);
      }
    }
  }

  @Action
  private async [GeneralAction.HandleCategoriesSearchResponse](payload: {
    responseData: SearchResponse;
  }) {
    this.context.commit(Mutators.HandleCategoriesSearchResponse, payload.responseData.category);
  }

  @Action
  private async [GeneralAction.HandleExternalSearchResponse](payload: {
    responseData: SearchResponse;
  }) {
    this.context.commit(Mutators.HandleExternalSearchResponse, payload.responseData.external);
  }

  @Action
  private async [GeneralAction.HandleProductSearchResponse](payload: {
    responseData: SearchResponse;
  }) {
    this.context.commit(Mutators.HandleProductSearchResponse, payload.responseData.product);
  }

  @Action
  private async [SearchAction.DoSearch](payload: {
    resetSearchContextData: boolean;
  } = { resetSearchContextData: true }) {
    if (payload.resetSearchContextData) {
      this.context.commit(Mutators.HandleUpdateSearchContextData);
    }
    await SearchModule.doSearch(
      this.context,
      this.activeSearchContextData,
      this.searchTerm,
      this.context.getters[BaseGetter.Locale],
      this.context.getters[BaseGetter.Country],
      this.context.getters[FilterGetter.AppliedFilterOptions],
      this.context.getters[SearchGetter.StrictSearch],
    );
  }

  @Action
  private async [SearchAction.DoListSearch](payload: {
    resetSearchContextData: boolean;
  } = { resetSearchContextData: true }) {
    if (payload.resetSearchContextData) {
      this.context.commit(Mutators.HandleUpdateSearchContextData);
    }
    await SearchModule.doListSearch(
      this.context,
      this.activeSearchContextData,
      this.searchTerm,
      this.context.getters[BaseGetter.Locale],
      this.context.getters[BaseGetter.Country],
      this.context.getters[FilterGetter.AppliedFilterOptions],
      this.context.getters[SearchGetter.StrictSearch],
    );
  }

  @Action
  private async [GeneralAction.Init](payload: InitDataExtended) {
    this.context.commit(Mutators.HandleUpdateSearchContext, {
      searchContext: payload.searchContext,
    });
    this.context.commit(Mutators.HandleUpdateSearchIncrement, {
      searchIncrement: payload.searchIncrement,
    });
    this.context.commit(Mutators.HandleUpdateSearchTerm, {
      searchTerm: payload.searchTerm,
    });
    this.context.commit(Mutators.HandleUpdateSearchContextData);

    if (this.searchTerm.length > 0) {
      await this.context.dispatch(SearchAction.DoSearch);
    }
  }

  @Action
  private async [SearchAction.SearchWithNewTerm](payload: { searchTerm: string }) {
    if (this.searchTerm === payload.searchTerm) {
      return;
    }

    this.context.commit(Mutators.HandleUpdateSearchTerm, payload);

    await this.context.dispatch(GeneralAction.SwitchSearchTerm, payload);

    await this.context.dispatch(SearchAction.DoSearch);
  }

  @Action
  private async [GeneralAction.SwitchFilters]() {
    await this.context.dispatch(SearchAction.DoSearch);
  }

  @Action
  private async [SearchAction.SetStrictSearch](strictSearch: number) {
    await this.context.commit(Mutators.HandleUpdateStrictSearch, strictSearch);
  }

  @Mutation
  private [Mutators.HandleIncrementOffset]() {
    this.activeSearchContextData.offset += this.searchIncrement;
    this.activeSearchContextData.loading = true;
  }

  @Mutation
  private [Mutators.HandleCategoriesIncrementOffset]() {
    this.activeCategoriesSearchContextData.offset += this.categoriesIncrement;
    this.activeCategoriesSearchContextData.loading = true;
  }

  @Mutation
  private [Mutators.HandleExternalIncrementOffset]() {
    this.activeExternalSearchContextData.offset += this.externalIncrement;
    this.activeExternalSearchContextData.loading = true;
  }

  @Mutation
  private [Mutators.HandleProductsIncrementOffset]() {
    this.activeProductSearchContextData.offset += this.productsIncrement;
    this.activeProductSearchContextData.loading = true;
  }

  @Action
  private async [SearchAction.LoadMore]() {
    this.context.commit(Mutators.HandleIncrementOffset);
    await this.context.dispatch(SearchAction.DoSearch, { resetSearchContextData: false });
  }

  @Action
  private async [SearchAction.ListLoadMore]() {
    this.context.commit(Mutators.HandleIncrementOffset);
    await this.context.dispatch(SearchAction.DoListSearch, { resetSearchContextData: false });
  }

  @Action
  private [SearchAction.CategoriesLoadMore]() {
    this.context.commit(Mutators.HandleCategoriesIncrementOffset);
    return new Promise<SearchResultElement[]>((resolve) => {
      SearchService.doSearch(SearchModule.buildSearchRequest(
        this.activeCategoriesSearchContextData,
        this.searchTerm,
        this.context.getters[BaseGetter.Locale],
        this.context.getters[BaseGetter.Country],
        this.context.getters[FilterGetter.AppliedFilterOptions],
        this.context.getters[SearchGetter.StrictSearch],
      )).then((responseData: SearchResponse) => {
        this.context.commit(Mutators.HandleCategoriesSearchResponse, responseData);
        resolve(responseData.elements);
      });
    });
  }

  @Action
  private [SearchAction.ExternalLoadMore]() {
    this.context.commit(Mutators.HandleExternalIncrementOffset);
    return new Promise<SearchResultElement[]>((resolve) => {
      SearchService.doSearch(SearchModule.buildSearchRequest(
        this.activeExternalSearchContextData,
        this.searchTerm,
        this.context.getters[BaseGetter.Locale],
        this.context.getters[BaseGetter.Country],
        this.context.getters[FilterGetter.AppliedFilterOptions],
        this.context.getters[SearchGetter.StrictSearch],
      )).then((responseData: SearchResponse) => {
        this.context.commit(Mutators.HandleExternalSearchResponse, responseData);
        resolve(responseData.elements);
      });
    });
  }

  @Action
  private [SearchAction.ProductLoadMore]() {
    this.context.commit(Mutators.HandleProductsIncrementOffset);
    return new Promise<SearchResultElement[]>((resolve) => {
      SearchService.doSearch(SearchModule.buildSearchRequest(
        this.activeProductSearchContextData,
        this.searchTerm,
        this.context.getters[BaseGetter.Locale],
        this.context.getters[BaseGetter.Country],
        this.context.getters[FilterGetter.AppliedFilterOptions],
        this.context.getters[SearchGetter.StrictSearch],
      )).then((responseData: SearchResponse) => {
        this.context.commit(Mutators.HandleProductSearchResponse, responseData);
        resolve(responseData.elements);
      });
    });
  }

  @Action
  private async [GeneralAction.RouteUpdate](payload: { routeData: RouteData }) {
    let performSearch = false;
    if (this.activeSearchContext !== payload.routeData.searchContext) {
      this.context.commit(Mutators.HandleUpdateSearchContext, {
        searchContext: payload.routeData.searchContext,
      });
      performSearch = true;
    }

    if (this.searchTerm !== payload.routeData.searchTerm) {
      this.context.commit(Mutators.HandleUpdateSearchTerm, {
        searchTerm: payload.routeData.searchTerm,
      });
      performSearch = true;
    }

    if (performSearch) {
      await this.context.dispatch(SearchAction.DoSearch);
    }
  }

  @Action
  private async [GeneralAction.SwitchSearchContext](payload: { searchContext: SupportedContext }) {
    this.context.commit(Mutators.HandleUpdateSearchContext, payload);
    await this.context.dispatch(SearchAction.DoSearch);
  }

  @Mutation
  private [Mutators.HandleSetViewed](payload: {
    viewedSearchResults: Map<string, SearchResultElement>;
  }) {
    payload.viewedSearchResults.forEach((searchResult: SearchResultElement) => {
      this.activeSearchContextData.elements[
        this.activeSearchContextData.elements.indexOf(searchResult)
      ].viewed = true;
    });
  }

  @Action
  private async [SearchAction.SetViewed](payload: {
    viewedSearchResults: Map<string, SearchResultElement>;
  }) {
    this.context.commit(Mutators.HandleSetViewed, payload);
  }
}
